/**
 * FilesStudio — ADR-111 IA-3 / ADR-132 / ADR-123 D8e: the Fleet Editor's
 * **Resources** area and an agent's `activities/` files. Resources remain the
 * fleet's shared knowledge base; agent activity files can be deleted through
 * the governed `agent_file` impact sweep.
 *
 * ADR-132 brings it to parity with the Agents / Skills / Workflows / Crons
 * facets, which have all had create AND delete since ADR-111/123:
 *
 *   - the rail is a real FOLDER TREE, not a flat list of slash-joined labels,
 *     because a folder you cannot see is a folder you cannot manage;
 *   - New file and New folder create into the selected folder;
 *   - selecting a file or a folder offers Delete through the shared ADR-123
 *     DeletePanel — plan → confirm → propose — so a resource delete gets the
 *     same recursive impact sweep and the same hard block on an executable
 *     reference as every other entity. A folder delete removes its whole
 *     subtree, exactly as an agent delete removes its overlay directory.
 *
 * A folder is created by committing a `.gitkeep` placeholder inside it, because
 * git tracks blobs and not directories. The placeholder is an implementation
 * detail of the folder's existence, so the tree hides it and shows the folder.
 *
 * Everything still goes through the same GET /api/fleet/tree, GET/POST
 * /api/fleet/file(/propose) and POST /api/fleet/delete/{plan,propose} path as
 * before: nothing is written outside a branch → PR → in-app improvement →
 * Approve & Deploy (ADR-010/046).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, agentFileTarget, AuthorTestsFailingError, CoreOwnedError, resourceTarget } from '../api/client'
import type { DeleteTarget, FailingCheck, FleetTree, ProposeSkillResult } from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { DeletePanel } from '../components/DeletePanel'
import { isPathStaged, useStagedPaths } from '../hooks/useStagedPaths'
import { StagedBadge } from '../components/StagedBadge'
import {
  GITKEEP, RESOURCES_SCOPE, ancestorDirs, buildResourceTree, countFiles, dirPaths, findDir,
  hasHiddenExtension, parentDir, validateNewPath,
} from './resourceTree'
import type { FilesScope, NewPathCheck, ResourceDir } from './resourceTree'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** What the operator has selected in the rail — a file to edit, or a folder. */
type Selection = { kind: 'file'; path: string } | { kind: 'dir'; path: string }

/** An in-progress create, scoped to the folder it lands in. */
interface Draft {
  kind: 'file' | 'folder'
  parent: string
}

export function FilesStudio({
  canPropose,
  scope = RESOURCES_SCOPE,
  initialPath,
}: {
  canPropose: boolean
  /** Which subtree to show (ADR-111 IA-7). Defaults to the fleet's `resources/`,
   *  so the Resources area is unchanged. */
  scope?: FilesScope
  /** Open this repo-relative file on mount (`?path=` on the files facet).
   *
   *  The workflow editor links here for a python step's `file:` body: reading
   *  that code used to mean knowing where it lived and finding it by hand, from
   *  a note that told the operator the tab existed and nothing more. */
  initialPath?: string
}) {
  const ROOT = scope.root
  // The rail shows a short name; `title` keeps the full repo path.
  const ROOT_LABEL = scope.label ?? scope.root
  const isAgentScope = scope.kind === 'agent'
  const [tree, setTree] = useState<FleetTree | null>(null)
  // ADR-111 D2: every entry here already carries an exact repo-relative
  // path (unlike Skills/Workflows, which only know a slug/name) — an exact
  // membership check is enough to badge it "staged — not yet live".
  const { paths: stagedPaths, refresh: refreshStaged } = useStagedPaths()
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set([ROOT]))
  const [search, setSearch] = useState('')
  const [treeCollapsed, setTreeCollapsed] = useState(false)  // #3: collapsible file navigator, like the other studios

  const [selected, setSelected] = useState<Selection | null>(null)
  const [selectedCoreOwned, setSelectedCoreOwned] = useState(false)
  const [original, setOriginal] = useState('')
  const [draft, setDraft] = useState('')
  const [fileLoading, setFileLoading] = useState(false)
  const [fileErr, setFileErr] = useState<string | null>(null)

  const [note, setNote] = useState('')
  const [proposing, setProposing] = useState(false)
  const [proposeErr, setProposeErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)

  // The create flow, deliberately separate state from the editor's: a half-typed
  // new file must not be confused with unsaved edits to an existing one.
  const [creating, setCreating] = useState<Draft | null>(null)
  const [newName, setNewName] = useState('')
  const [newContent, setNewContent] = useState('')

  const loadTree = () => {
    setLoading(true)
    api.getFleetTree()
      .then(t => setTree(t))
      .catch(e => setLoadErr(em(e)))
      .finally(() => setLoading(false))
  }
  useEffect(loadTree, [])

  const agentNode = isAgentScope
    ? tree?.agents.find(a => a.kind === 'agent' && a.agent === scope.agent)
    : undefined

  // The paths the tree is built from: what exists on develop, UNION the
  // resources/ paths already staged in an open change. Without the union, a file
  // an operator just proposed would vanish from the rail until the change was
  // applied, which reads as "it didn't work" (it did — it is awaiting approval).
  const allPaths = useMemo(() => {
    const node = isAgentScope ? agentNode : tree?.agents.find(a => a.kind === 'resources')
    const paths = new Set((node?.files ?? []).map(f => f.path))
    for (const p of stagedPaths) {
      if (p.startsWith(ROOT + '/')) paths.add(p)
    }
    // Drop the kinds another facet owns (ADR-111 IA-7): a workflow's .yaml is
    // authored on Workflows, and showing it here would give one artifact two
    // editors. Filtered BEFORE the tree is built so a folder holding nothing else
    // does not appear as an empty folder either.
    for (const p of [...paths]) {
      if (hasHiddenExtension(p, scope.hiddenExtensions)) paths.delete(p)
    }
    return paths
  }, [tree, agentNode, stagedPaths, isAgentScope, scope.hiddenExtensions, ROOT])

  const root = useMemo(() => {
    const q = search.trim().toLowerCase()
    const paths = [...allPaths]
    return buildResourceTree(q ? paths.filter(p => p.toLowerCase().includes(q)) : paths, ROOT)
  }, [allPaths, search, ROOT])

  const deleteTargetFor = (path: string): DeleteTarget => isAgentScope
    ? agentFileTarget(scope.agent!, path)
    : resourceTarget(path)

  // A search whose matches sit behind collapsed folders looks like no matches, so
  // filtering expands everything. Clearing the search leaves those folders open
  // rather than snapping shut — collapsing a folder the operator can now see
  // would undo work they just did.
  useEffect(() => {
    if (search.trim()) setExpanded(new Set(dirPaths(root)))
  }, [search, root])

  const dirty = selected?.kind === 'file' && !selectedCoreOwned && draft !== original

  const confirmDiscard = () =>
    !dirty || window.confirm('Discard unsaved changes to the current file?')

  const toggleDir = (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const resetPanels = () => {
    setResult(null)
    setProposeErr(null)
    setFailingChecks([])
    setFileErr(null)
    setNote('')
    setCreating(null)
  }

  const selectDir = (path: string) => {
    if (!confirmDiscard()) return
    toggleDir(path)
    setSelected({ kind: 'dir', path })
    resetPanels()
  }

  const selectFile = (path: string) => {
    if (selected?.kind === 'file' && path === selected.path) return
    if (!confirmDiscard()) return
    setSelected({ kind: 'file', path })
    setSelectedCoreOwned(false)
    setFileLoading(true)
    resetPanels()
    api.getFleetFile(path)
      .then(f => {
        setOriginal(f.content)
        setDraft(f.content)
        setSelectedCoreOwned(f.core_owned)
      })
      .catch(e => setFileErr(em(e)))
      .finally(() => setFileLoading(false))
  }

  // Open `initialPath` once per path. Deliberately not gated on the tree having
  // loaded: getFleetFile takes a path, so the file opens immediately and the
  // rail catches up — a deep link should not wait on a directory listing.
  const openedInitial = useRef<string | null>(null)
  useEffect(() => {
    if (!initialPath || openedInitial.current === initialPath) return
    openedInitial.current = initialPath
    selectFile(initialPath)
    // selectFile is a stable in-component closure; listing it would re-run this
    // on every render and re-open the file over the operator's own selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPath])

  const propose = async () => {
    if (selected?.kind !== 'file') return
    setProposing(true)
    setProposeErr(null)
    setFailingChecks([])
    try {
      const r = await api.proposeFleetFile(selected.path, draft, note.trim() || undefined)
      setResult(r)
      setOriginal(draft)
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else if (e instanceof CoreOwnedError) setProposeErr(e.message || 'This file is core-owned and cannot be edited here — propose the change to core instead.')
      else setProposeErr(em(e))
    } finally {
      setProposing(false)
    }
  }

  /** The folder a create lands in: the selected folder, or the selected file's. */
  const activeDir = (): string => {
    if (!selected) return ROOT
    return selected.kind === 'dir' ? selected.path : parentDir(selected.path, ROOT)
  }

  const startCreate = (kind: 'file' | 'folder') => {
    if (!confirmDiscard()) return
    const parent = activeDir()
    setCreating({ kind, parent })
    setNewName('')
    setNewContent('')
    setResult(null)
    setProposeErr(null)
    setFailingChecks([])
    setNote('')
  }

  const newPathCheck = creating
    ? validateNewPath(newName, creating.kind, creating.parent, allPaths, scope.hiddenExtensions)
    : { error: undefined, path: undefined }

  const submitCreate = async () => {
    if (!creating || !newPathCheck.path) return
    // A folder is created by its placeholder; a file by its own content.
    const target = creating.kind === 'folder' ? `${newPathCheck.path}/${GITKEEP}` : newPathCheck.path
    const content = creating.kind === 'folder' ? '' : newContent
    setProposing(true)
    setProposeErr(null)
    setFailingChecks([])
    try {
      const r = await api.proposeFleetFile(target, content, note.trim() || undefined)
      setResult(r)
      // The form STAYS mounted, showing the outcome behind a Done button. Closing
      // it here would drop the operator back on an empty pane with no sign the
      // propose landed — which reads as "it didn't work".
      // Reveal what was just created: the folder itself, or a new file's folder —
      // and every ancestor, since a collapsed one higher up would still hide it.
      const reveal = creating.kind === 'folder' ? newPathCheck.path : parentDir(newPathCheck.path, ROOT)
      setExpanded(prev => new Set([...prev, ...ancestorDirs(reveal, ROOT)]))
      loadTree()
      // The new path is staged, not on develop: only the staged set can show it.
      refreshStaged()
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else if (e instanceof CoreOwnedError) setProposeErr(e.message || 'That path is core-owned and cannot be created here.')
      else setProposeErr(em(e))
    } finally {
      setProposing(false)
    }
  }

  const onDeleted = () => {
    // The selection is deliberately KEPT: DeletePanel renders its own "sent for
    // review" outcome, and clearing the selection here would unmount that the
    // instant it appeared. Nothing is removed until the change is applied, so
    // the file/folder still being there — now badged staged — is the truth.
    loadTree()
    refreshStaged()
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            {/* In the agent scope this renders INSIDE the Fleet Editor shell,
                which already carries the agents rail and the facet tabs — a
                second "← Agents" breadcrumb there is duplicate navigation. */}
            {!isAgentScope && (
              <>
                <Link to="/fleet-editor" className="text-sm text-gray-500 hover:text-gray-800">← Agents</Link>
                <span className="text-gray-300" aria-hidden="true">/</span>
              </>
            )}
            <h1 className="text-2xl font-semibold text-gray-900">
              {isAgentScope ? 'Files' : 'Resources'}
            </h1>
          </div>
          {isAgentScope ? (
            <p className="text-sm text-gray-500 mt-1">
The <code>*.py</code> step bodies in <code>{ROOT}/</code> — the code an ADR-080
              python task runs, and the one thing in this fleet no editor reached, so
              reading it used to mean opening GitHub. Everything a typed facet owns is
              deliberately absent: the workflow <code>.yaml</code> beside these files
              belongs to <strong>Workflows</strong>, as <code>SOUL.md</code> does to
              Persona and <code>crons.yaml</code> to Crons. One artifact, one editor —
              two ways to edit one file is how the two disagree. Adding and editing are
              proposed for in-app review; nothing changes until you approve it.
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">
              The fleet's shared knowledge base — reference material in <code>/resources</code> that any agent (or the brain) can draw on. Everything else is edited in context on an <Link to="/fleet-editor" className="underline">Agent</Link>'s facets, not here. Adding, editing and deleting are all proposed for in-app review; nothing changes until you approve it.
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto space-y-4">
        <div className="flex gap-4 min-h-[28rem]">
          {/* Left rail: the resources folder tree (collapsible, like the workflow/skill lists) */}
          {treeCollapsed ? (
            <div className="w-10 shrink-0 flex flex-col items-center rounded-lg border border-gray-200 bg-white pt-2">
              <button type="button" onClick={() => setTreeCollapsed(false)} title="Show file navigator"
                aria-label="Show file navigator" className="text-gray-500 hover:text-gray-800">›</button>
              <span className="mt-2 text-[10px] text-gray-400" style={{ writingMode: 'vertical-rl' }}>{isAgentScope ? 'Files' : 'Resources'}</span>
            </div>
          ) : (
          <div className="w-72 shrink-0 flex flex-col rounded-lg border border-gray-200 bg-white">
            <div className="p-2 border-b border-gray-100 flex items-center gap-1.5">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={isAgentScope ? 'Filter files…' : 'Filter resources…'} className="flex-1 min-w-0 text-sm border border-gray-300 rounded px-2 py-1" />
              <button type="button" onClick={() => setTreeCollapsed(true)} title="Hide file navigator"
                aria-label="Hide file navigator" className="shrink-0 text-gray-400 hover:text-gray-700">‹</button>
            </div>
            {canPropose && (
              <div className="px-2 py-1.5 border-b border-gray-100 flex items-center gap-2">
                <button type="button" onClick={() => startCreate('file')} data-testid="new-resource-file"
                  className="text-xs text-emerald-700 hover:text-emerald-900"
                  title={`Add a file in ${activeDir()}`}>＋ File</button>
                <button type="button" onClick={() => startCreate('folder')} data-testid="new-resource-folder"
                  className="text-xs text-emerald-700 hover:text-emerald-900"
                  title={`Add a folder in ${activeDir()}`}>＋ Folder</button>
                <span className="ml-auto text-[10px] text-gray-400 font-mono truncate" title={activeDir()}>
                  in {activeDir()}
                </span>
              </div>
            )}
            <div className="flex-1 overflow-auto py-1">
              {loading ? (
                <p className="text-xs text-gray-400 p-2 animate-pulse">Loading…</p>
              ) : loadErr ? (
                <p className="text-xs text-red-600 p-2 whitespace-pre-wrap break-words">{loadErr}</p>
              ) : countFiles(root) === 0 && root.dirs.length === 0 ? (
                <p className="text-xs text-gray-500 p-2">
                  {isAgentScope
                    ? (search.trim() ? 'No files match.' : 'No files yet.')
                    : (search.trim() ? 'No resources match.' : 'No resources yet.')}
                </p>
              ) : (
                <ul>
                  {/* The root is a selectable row of its own: it is the folder a
                      create defaults into, so it has to be reachable. */}
                  <li>
                    <button type="button" onClick={() => selectDir(ROOT)}
                      aria-current={selected?.kind === 'dir' && selected.path === ROOT}
                      style={indentStyle(0)} title={ROOT}
                      className={`w-full flex items-center gap-1.5 pr-2 py-1 text-xs font-semibold ${
                        selected?.kind === 'dir' && selected.path === ROOT
                          ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700 hover:bg-gray-50'
                      }`}>
                      <span className="text-gray-400">{expanded.has(ROOT) ? '▾' : '▸'}</span>
                      <span className="truncate font-mono">{ROOT_LABEL}/</span>
                    </button>
                    {expanded.has(ROOT) && (
                      <DirRows dir={root} level={1} expanded={expanded} selected={selected}
                        stagedPaths={stagedPaths} onSelectDir={selectDir} onSelectFile={selectFile} />
                    )}
                  </li>
                </ul>
              )}
            </div>
          </div>
          )}

          {/* Center: the create form, a folder panel, or the file editor */}
          <div className="flex-1 min-w-0 flex flex-col rounded-lg border border-gray-200 bg-white">
            {creating ? (
              <CreateForm
                draft={creating}
                name={newName}
                onName={setNewName}
                content={newContent}
                onContent={setNewContent}
                note={note}
                onNote={setNote}
                check={newPathCheck}
                busy={proposing}
                err={proposeErr}
                failingChecks={failingChecks}
                result={result}
                onCancel={() => setCreating(null)}
                onSubmit={() => void submitCreate()}
              />
            ) : selected?.kind === 'dir' ? (
              <FolderPanel
                path={selected.path}
                dir={findDir(root, selected.path)}
                rootPath={ROOT}
                target={deleteTargetFor(selected.path)}
                coreOwned={isAgentScope && Boolean(agentNode?.core_owned)}
                canPropose={canPropose}
                result={result}
                onNewFile={() => startCreate('file')}
                onNewFolder={() => startCreate('folder')}
                onDeleted={onDeleted}
              />
            ) : !selected ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                Select a {isAgentScope ? 'file' : 'resource'} to view or edit it{canPropose ? ', or add a new one.' : '.'}
              </div>
            ) : (
              <div className="flex-1 flex flex-col min-h-0">
                {/* Same shape as FolderPanel's header: the panel is a direct child of a
                    flex-wrap row so the opened plan drops to its own full-width line. */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100 text-xs">
                  <span className="flex-1 min-w-0 font-mono text-gray-700 truncate" title={selected.path}>{selected.path}</span>
                  {selectedCoreOwned && (
                    <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">
                      core-owned — read-only
                    </span>
                  )}
                  {canPropose && !selectedCoreOwned && !agentNode?.core_owned && (
                    <DeletePanel key={selected.path} target={deleteTargetFor(selected.path)} label="Delete file" onProposed={onDeleted} />
                  )}
                </div>
                {selectedCoreOwned && (
                  <p className="px-3 pt-2 text-xs text-gray-500 italic">
                    This file is core-owned (ADR-103). It can only be changed through core — propose the change there instead of here.
                  </p>
                )}
                {fileLoading ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-gray-400 animate-pulse">Loading…</div>
                ) : fileErr ? (
                  <p className="p-3 text-xs text-red-600 whitespace-pre-wrap break-words">{fileErr}</p>
                ) : (
                  <>
                    <textarea
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      disabled={selectedCoreOwned}
                      spellCheck={false}
                      className="flex-1 min-h-0 w-full resize-none font-mono text-xs p-3 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    {!selectedCoreOwned && (
                      <div className="border-t border-gray-100 p-3 space-y-2">
                        <ProposeResult result={result} />
                        {proposeErr && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{proposeErr}</p>}
                        {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}
                        <div className="flex items-center gap-2">
                          {canPropose ? (
                            <button type="button" disabled={proposing || !dirty} onClick={() => void propose()}
                              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                              title={!dirty ? 'No changes to propose' : undefined}>
                              {proposing ? 'Proposing…' : 'Propose'}
                            </button>
                          ) : <span className="text-xs text-gray-500">Only a fleet owner can propose a file change.</span>}
                          <input type="text" value={note} onChange={e => setNote(e.target.value)}
                            placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * DirRows renders one folder's children, recursively. Folders sort before files
 * and both are indented by depth, so the nesting the paths always encoded is
 * finally visible — the flat `kb/pricing.md` labels the previous version showed
 * gave an operator no way to act on `kb` itself.
 */
function DirRows(props: {
  /** The folder whose CHILDREN this renders; its own row belongs to the caller. */
  dir: ResourceDir
  /** Indent level for those children. Folders and files share it — a sibling
   *  file drawn one step deeper than a sibling folder reads as nested under it. */
  level: number
  expanded: Set<string>
  selected: Selection | null
  stagedPaths: Set<string>
  onSelectDir: (path: string) => void
  onSelectFile: (path: string) => void
}) {
  const { dir, level, expanded, selected, stagedPaths, onSelectDir, onSelectFile } = props
  return (
    <ul>
      {dir.dirs.map(child => {
        const isSelected = selected?.kind === 'dir' && selected.path === child.path
        return (
          <li key={child.path}>
            <button type="button" onClick={() => onSelectDir(child.path)}
              aria-current={isSelected} style={indentStyle(level)} title={child.path}
              className={`w-full flex items-center gap-1.5 pr-2 py-1 text-xs font-medium ${
                isSelected ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700 hover:bg-gray-50'
              }`}>
              <span className="text-gray-400">{expanded.has(child.path) ? '▾' : '▸'}</span>
              <span className="truncate font-mono">{child.name}/</span>
            </button>
            {expanded.has(child.path) && (
              <DirRows {...props} dir={child} level={level + 1} />
            )}
          </li>
        )
      })}
      {dir.files.map(f => {
        const isSelected = selected?.kind === 'file' && selected.path === f.path
        return (
          <li key={f.path}>
            <button type="button" onClick={() => onSelectFile(f.path)} aria-current={isSelected}
              style={indentStyle(level)} title={f.path}
              className={`w-full text-left pr-2 py-1 text-xs font-mono flex items-center gap-1.5 ${
                isSelected ? 'bg-indigo-50 text-indigo-800' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              <span className="truncate">{f.name}</span>
              {isPathStaged(stagedPaths, f.path) && <StagedBadge />}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function indentStyle(level: number) {
  return { paddingLeft: `${0.5 + level * 0.75}rem` }
}

/**
 * FolderPanel is the detail view for a selected folder. It exists so a folder is
 * a first-class thing an operator can act on: add into it, or delete it and
 * everything under it in one reviewed change.
 */
function FolderPanel({
  path, dir, rootPath, target, coreOwned, canPropose, result, onNewFile, onNewFolder, onDeleted,
}: {
  path: string
  dir: ResourceDir | null
  rootPath: string
  /** Resource or agent_file target for this non-root folder. */
  target: DeleteTarget
  /** Agent activity folders cannot be changed when their owning agent is core-owned. */
  coreOwned: boolean
  canPropose: boolean
  result: ProposeSkillResult | null
  onNewFile: () => void
  onNewFolder: () => void
  onDeleted: () => void
}) {
  const files = dir ? countFiles(dir) : 0
  const folders = dir ? dirPaths(dir).length - 1 : 0
  const isRoot = path === rootPath
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="resource-folder-panel">
      {/* flex-wrap, and the panel a direct child of the row: opened, it takes a line
          of its own at the card's width instead of growing the row past it. */}
      <div className="px-3 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2 text-xs">
        <span className="flex-1 min-w-0 font-mono text-gray-700 truncate" title={path}>{path}/</span>
        {/* The knowledge base's own root is not a resource — deleting it would
            delete the area, not something in it. */}
        {canPropose && !isRoot && !coreOwned && (
          <DeletePanel key={path} target={target} label="Delete folder" onProposed={onDeleted} />
        )}
      </div>
      <div className="p-3 space-y-3">
        <p className="text-xs text-gray-600">
          {files === 0 && folders === 0
            ? 'This folder is empty.'
            : `${files} file${files === 1 ? '' : 's'}${folders > 0 ? ` in ${folders} sub-folder${folders === 1 ? '' : 's'}` : ''}.`}
        </p>
        {!isRoot && (
          <p className="text-[11px] text-gray-500">
            {target.kind === 'agent_file'
              ? "Deleting this folder removes everything under it, in one change. The impact is worked out first: a file a workflow's python step runs blocks the delete, and a step body that seems to import or read one is listed for you to check before you propose."
              : 'Deleting this folder removes everything under it, in one change. The impact is worked out first — a resource still read by a workflow or a cron blocks the delete rather than breaking at run time.'}
          </p>
        )}
        {canPropose && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={onNewFile}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
              ＋ File here
            </button>
            <button type="button" onClick={onNewFolder}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
              ＋ Folder here
            </button>
          </div>
        )}
        <ProposeResult result={result} />
      </div>
    </div>
  )
}

/** CreateForm is the new-file / new-folder step. */
function CreateForm({
  draft, name, onName, content, onContent, note, onNote, check, busy, err, failingChecks, result, onCancel, onSubmit,
}: {
  draft: Draft
  name: string
  onName: (v: string) => void
  content: string
  onContent: (v: string) => void
  note: string
  onNote: (v: string) => void
  check: NewPathCheck
  busy: boolean
  err: string | null
  failingChecks: FailingCheck[]
  result: ProposeSkillResult | null
  onCancel: () => void
  onSubmit: () => void
}) {
  const isFolder = draft.kind === 'folder'

  // Once it is proposed there is nothing left to fill in, and re-submitting the
  // same name would open a second identical change. Show the outcome instead.
  if (result) {
    return (
      <div className="flex-1 flex flex-col min-h-0" data-testid="create-resource-form">
        <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">
            New {isFolder ? 'folder' : 'file'} proposed
          </span>
          <button type="button" onClick={onCancel} data-testid="new-resource-done"
            className="text-xs text-gray-500 hover:text-gray-700">Done</button>
        </div>
        <div className="p-3 space-y-3">
          <ProposeResult result={result} />
          <p className="text-[11px] text-gray-500">
            It shows in the tree marked <em>staged</em> until the change is applied.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="create-resource-form">
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-800">
          New {isFolder ? 'folder' : 'file'} in <span className="font-mono text-xs">{draft.parent}/</span>
        </span>
        <button type="button" onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
      <div className="p-3 space-y-3 flex-1 min-h-0 flex flex-col">
        <div className="space-y-1">
          <label className="block text-xs text-gray-600" htmlFor="new-resource-name">
            {isFolder ? 'Folder name' : 'File name'} — a nested path like{' '}
            <span className="font-mono">{isFolder ? 'pricing/2026' : 'pricing/rates.md'}</span> creates the folders it needs
          </label>
          <input id="new-resource-name" type="text" value={name} onChange={e => onName(e.target.value)}
            data-testid="new-resource-name" spellCheck={false} autoFocus
            placeholder={isFolder ? 'pricing' : 'pricing.md'}
            className="w-full text-sm font-mono border border-gray-300 rounded px-2 py-1" />
          {check.path && <p className="text-[11px] text-gray-500 font-mono">{check.path}</p>}
          {name.trim() && check.error && (
            <p className="text-xs text-red-600" data-testid="new-resource-error">{check.error}</p>
          )}
          {isFolder && (
            <p className="text-[11px] text-gray-500">
              Git stores folders only through their contents, so this adds a <span className="font-mono">{GITKEEP}</span>{' '}
              placeholder inside it. Add real files and the placeholder stops mattering.
            </p>
          )}
        </div>

        {!isFolder && (
          <textarea value={content} onChange={e => onContent(e.target.value)} spellCheck={false}
            data-testid="new-resource-content" placeholder="File contents…"
            className="flex-1 min-h-[12rem] w-full resize-none font-mono text-xs p-2 border border-gray-200 rounded outline-none" />
        )}

        {/* No ProposeResult here — a landed propose renders the outcome view above. */}
        {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
        {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

        <div className="flex items-center gap-2">
          <button type="button" disabled={busy || !check.path} onClick={onSubmit}
            data-testid="new-resource-submit"
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
            {busy ? 'Proposing…' : `Propose new ${isFolder ? 'folder' : 'file'}`}
          </button>
          <input type="text" value={note} onChange={e => onNote(e.target.value)}
            placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
        </div>
      </div>
    </div>
  )
}

/** ProposeResult renders the one shared "sent for review" outcome. */
function ProposeResult({ result }: { result: ProposeSkillResult | null }) {
  if (!result) return null
  if (result.improvement_warning) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
        <p>{result.improvement_warning}</p>
      </div>
    )
  }
  return (
    <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
      <p>Sent for review. Nothing changes until you approve it.</p>
      {result.improvement_id
        ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
        : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
    </div>
  )
}
